import React, { useState, useRef, useCallback } from 'react';
import { Shield, CheckCircle } from 'lucide-react';

interface SliderCaptchaProps {
  onVerify: (success: boolean) => void;
}

export default function SliderCaptcha({ onVerify }: SliderCaptchaProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [verified, setVerified] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);

  const getMaxPosition = useCallback(() => {
    if (!trackRef.current) return 0;
    return trackRef.current.offsetWidth - 40;
  }, []);

  const startDrag = useCallback((clientX: number) => {
    if (verified) return;
    setIsDragging(true);
    startXRef.current = clientX - sliderPosition;
  }, [verified, sliderPosition]);

  const moveDrag = useCallback((clientX: number) => {
    if (!isDragging) return;
    const trackWidth = getMaxPosition();
    let newPosition = clientX - startXRef.current;
    newPosition = Math.max(0, Math.min(newPosition, trackWidth));
    setSliderPosition(newPosition);

    if (newPosition >= trackWidth * 0.9) {
      setVerified(true);
      setIsDragging(false);
      onVerify(true);
    }
  }, [isDragging, onVerify, getMaxPosition]);

  const endDrag = useCallback(() => {
    if (!verified) {
      const trackWidth = getMaxPosition();
      if (sliderPosition < trackWidth * 0.9) {
        setSliderPosition(0);
      }
    }
    setIsDragging(false);
  }, [verified, sliderPosition, getMaxPosition]);

  // 鼠标事件
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startDrag(e.clientX);
  }, [startDrag]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    moveDrag(e.clientX);
  }, [moveDrag]);

  const handleMouseUp = useCallback(() => {
    endDrag();
  }, [endDrag]);

  // 触摸事件
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    startDrag(e.touches[0].clientX);
  }, [startDrag]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    moveDrag(e.touches[0].clientX);
  }, [moveDrag]);

  const handleTouchEnd = useCallback(() => {
    endDrag();
  }, [endDrag]);

  return (
    <div className="bg-gray-100 rounded-lg p-3">
      {verified ? (
        <div className="flex items-center justify-center gap-2 py-2 text-green-600">
          <CheckCircle className="w-5 h-5" />
          <span>验证通过</span>
        </div>
      ) : (
        <>
          <div className="text-xs text-gray-500 text-center mb-2">请拖动滑块完成验证</div>
          <div
            ref={trackRef}
            className="relative h-10 bg-gray-200 rounded-full cursor-pointer select-none"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
              <Shield className="w-4 h-4 mr-1" />
              拖动滑块到右侧
            </div>
            <div
              className="absolute top-0 left-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing shadow-md select-none touch-none"
              style={{ transform: `translateX(${sliderPosition}px)` }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <div className="w-2 h-4 border-r-2 border-white mr-1" />
              <div className="w-2 h-4 border-r-2 border-white" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}